import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useMsal } from '@azure/msal-react';
import { AI_ENGINE_URL } from '../utils/apiConfig';
import { authFetch } from '../utils/authHelpers';
import { getDevSession } from '../utils/devSession';

const UserRoleContext = createContext({
  role: 'team',
  permissions: {},
  loading: true,
});

export function UserRoleProvider({ children }) {
  const { instance, accounts } = useMsal();
  const [role, setRole] = useState('team');
  const [error, setError] = useState(null);
  const herlaadRef = useRef(() => {});
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);

  const devSession = getDevSession();
  const devEmail = devSession?.email || null;
  // Under a dev-login session there is no MSAL account; use the dev email so
  // /users/me returns the picked role's permissions (the AI engine honours the
  // dev token). Falls back to the MSAL account for normal logins.
  // Het ACTIEVE account (daarvan gebruikt authFetch het token), anders het enige/eerste.
  const actief = instance?.getActiveAccount?.() || accounts?.[0] || null;
  const huidigEmail = devEmail || actief?.username?.toLowerCase() || null;
  // Identiteit waarvoor de rechten gelden: account-id + e-mail (dev: de dev-e-mail).
  const identiteit = devEmail ? `dev:${devEmail}` : (huidigEmail ? `${actief?.homeAccountId || ''}|${huidigEmail}` : null);
  // Voor wie zijn de rechten geladen? Komt er na de login-redirect (of stil inloggen)
  // een account bij, dan zijn de rechten van dát account er nog niet: tot dan "laden".
  // Zonder dit zag ProtectedRoute één render lang loading=false + lege rechten en
  // stuurde een deep link (bv. /wijzigingen) naar home.
  const [geladenVoor, setGeladenVoor] = useState(null);

  useEffect(() => {
    const email = huidigEmail;
    const voor = identiteit;
    if (!email) {
      // Uitgelogd/account weg: geen rechten van het vorige account laten staan.
      setRole('team');
      setPermissions({});
      setGeladenVoor(null);
      setLoading(false);
      return;
    }

    let cancelled = false;

    // Fetch permissions from database
    const fetchPermissions = () =>
      authFetch(`${AI_ENGINE_URL}/api/v1/permissions/users/me?email=${encodeURIComponent(email)}`)
        .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
        .then(data => {
          if (cancelled) return;
          setRole(data.role || 'team');
          setPermissions(data.permissions || {});
          setError(null);
        })
        .catch((err) => {
          if (cancelled) return;
          console.warn('[UserRole] Could not fetch permissions, using defaults:', err.message);
          setRole('team');
          setPermissions({});
          setError(err.message || 'onbekende fout'); // FE-12: fail-closed blijft, maar zichtbaar
        })
        .finally(() => { if (!cancelled) { setGeladenVoor(voor); setLoading(false); } });

    herlaadRef.current = fetchPermissions;
    fetchPermissions();

    // Ververs de rechten wanneer het tabblad weer focus krijgt, zodat een
    // door een admin gewijzigde rol/recht doorkomt ZONDER opnieuw in te loggen.
    // Getthrottled (max. 1×/30s) om onnodige calls te voorkomen.
    let lastFetch = Date.now();
    const onVisible = () => {
      if (document.visibilityState === 'visible' && Date.now() - lastFetch > 30000) {
        lastFetch = Date.now();
        fetchPermissions();
      }
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [identiteit]); // eslint-disable-line react-hooks/exhaustive-deps -- huidigEmail volgt uit identiteit

  // Rechten gelden alleen voor de identiteit waarvoor ze geladen zijn; tot dan leeg + laden.
  // Zonder identiteit (uitgelogd) direct leeg — niet wachten op de reset in het effect.
  const nogNietGeladen = !!identiteit && geladenVoor !== identiteit;
  const leeg = !identiteit || nogNietGeladen;
  return (
    <UserRoleContext.Provider value={{
      role: leeg ? 'team' : role,
      permissions: leeg ? {} : permissions,
      loading: loading || nogNietGeladen, error, herlaad: () => herlaadRef.current() }}>
      {children}
    </UserRoleContext.Provider>
  );
}

export function useUserRole() {
  return useContext(UserRoleContext);
}
